import * as Api from '@ViewModels';
import { useAccountContext } from '../../../../admin/components/blogs/AccountContext';
import { css } from 'aphrodite';
import moment from 'moment-timezone';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { toast } from 'sonner';
import { useDebounceValue } from '../../../../hooks/useDebounceValue';
import { useSelectionState } from '../../../../hooks/useSelectionState';
import { useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import {
	invalidateAllScheduledMeetingFilters,
	invalidateAllScheduledMeetingStats,
	useExportScheduledMeetingsMutation,
	useScheduledMeetingContactsMutation,
	useScheduledMeetingFilter,
} from '../../../../queries';
import { BreadcrumbBar } from '../../../components-v2/BreadcrumbBar';
import { Column, Page } from '../../../components-v2/PageLayout';
import { Checkbox } from '../../../components/Checkbox';
import {
	Databoard,
	DataboardActions,
	DataboardBody,
	DataboardFilters,
	DataboardNoResults,
	DataboardStats,
} from '../../../components/Databoard';
import { MeetingDateFilterDropdown } from '../../../components/dataBoards/meetings/MeetingDateFilterDropdown';
import { MeetingDetailsFlyout } from '../../../components/dataBoards/meetings/MeetingDetailsFlyout';
import { MeetingHostFilterDropdown } from '../../../components/dataBoards/meetings/MeetingHostFilterDropdown';
import { MeetingStatusFilterDropdown } from '../../../components/dataBoards/meetings/MeetingStatusFilterDropdown';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '../../../components/DataGrid';
import { Badge } from '../../../components/dataBoards/Badge';
import { EntityChip } from '../../../components/chips/EntityChip';
import { ExportConfirmationModalV2 } from '../../../components/ExportConfirmation';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components-v2/Popover';
import { FabContext } from '../../../components/FabContext';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { MultiContainerHeader } from '../../../components/MultiContainerHeader';
import { LinkIcon } from '../../../components/svgs/icons/LinkIcon';
import { LocationIcon } from '../../../components/svgs/icons/LocationIcon';
import { PhoneIcon } from '../../../components/svgs/icons/PhoneIcon';
import { SearchIcon } from '../../../components/svgs/icons/SearchIcon';
import { SimpleTooltip } from '../../../components/Tooltip';
import { TextInput } from '../../../components/TextInput';
import { UsersActionBar } from '../../../components/UsersActionBar';
import { ROUTES } from '../../../constants/routes';
import { brandPrimary, navigation } from '../../../styles/colors';
import { baseStyleSheet } from '../../../styles/styles';
import { styleSheet as dbStyleSheet } from '../styles';
import {
	MEETING_STATUS_CONFIG,
	getLocationDetail,
	getLocationTypeLabel,
} from '../../../components/dataBoards/meetings/utils';
import { Button } from '../../../components-v2/Button';
import { ScheduleMeetingIcon } from '../../../components/svgs/icons/ScheduleMeetingIcon';
import { TableLayoutIcon } from '../../../components/svgs/icons/TableLayoutIcon';
import { MeetingsCalendarView } from './MeetingsCalendarView';
import { getSendEmailGuardResult } from './sendEmailGuard';
import { styleSheet } from './styles';

type BoardView = 'list' | 'calendar';

/** Find the host participant */
const getHost = (meeting: Api.IScheduledMeeting): Api.IParticipant | undefined => {
	return meeting.participants?.find(p => p.isHost);
};

/** Get host display name */
const getHostName = (host?: Api.IParticipant): string => {
	if (!host) return '-';
	if (host.user) {
		return `${host.user.firstName ?? ''} ${host.user.lastName ?? ''}`.trim() || '-';
	}
	return '-';
};

/** Get contact participants from a meeting */
const getContactParticipantsFromMeeting = (meeting: Api.IScheduledMeeting): Api.IContactParticipant[] => {
	return (meeting.participants ?? []).filter(
		(p): p is Api.IContactParticipant => (p as Api.IContactParticipant)._type === 'ContactParticipant'
	);
};

const localTzAbbr = moment.tz(moment.tz.guess()).zoneAbbr();

/** Format start/end as a combined date/time string with viewer's timezone */
const formatDateRange = (meeting: Api.IScheduledMeeting): string => {
	const { startDate, endDate } = meeting;
	if (!startDate) return '-';
	const start = moment(startDate);
	if (!endDate) return `${start.format('MMM D, h:mm A')} ${localTzAbbr}`;
	const end = moment(endDate);
	if (start.isSame(end, 'day')) {
		return `${start.format('MMM D, h:mm')} - ${end.format('h:mm A')} ${localTzAbbr}`;
	}
	return `${start.format('MMM D, h:mm A')} - ${end.format('MMM D, h:mm A')} ${localTzAbbr}`;
};

/** Render a location icon with tooltip showing detail */
const LocationDisplay = ({ location }: { location?: Api.IMeetingLocation }) => {
	if (!location?._type) return <span>-</span>;
	const detail = getLocationDetail(location);
	const label = getLocationTypeLabel(location);
	const tooltipText = detail ? `${label}: ${detail}` : label;

	const icon = (() => {
		switch (location._type) {
			case Api.MeetingLocation.Virtual:
				return <LinkIcon size={16} fillColor={navigation} />;
			case Api.MeetingLocation.Phone:
				return <PhoneIcon fill={navigation} />;
			case Api.MeetingLocation.InPerson:
				return <LocationIcon width={16} height={16} fill={navigation} />;
			default:
				return <span>{label}</span>;
		}
	})();

	return (
		<SimpleTooltip title={tooltipText}>
			<span>{icon}</span>
		</SimpleTooltip>
	);
};

/** Return a short label for the meeting source */
const getSourceLabel = (meeting: Api.IScheduledMeeting): string => {
	const source = meeting.creationContext?.source;
	if (!source || source === Api.MeetingSource.Unknown) return '-';
	switch (source) {
		case Api.MeetingSource.Direct:
			return 'Direct';
		case Api.MeetingSource.Website:
			return 'Website';
		case Api.MeetingSource.Agent:
			return 'Agent';
		default:
			return '-';
	}
};

/** Extract contact participants from a meeting as projected contacts */
const getContactParticipants = (meeting: Api.IScheduledMeeting): Api.IProjectedContact[] => {
	return (meeting.participants ?? [])
		.filter((p): p is Api.IContactParticipant => (p as Api.IContactParticipant)._type === 'ContactParticipant')
		.filter(p => !!p.contactId)
		.map(
			p =>
				p.contact ?? {
					id: p.contactId!,
					creatorId: '',
					firstName: p.firstName,
					lastName: p.lastName,
					emailAddresses: p.emailAddress ? [{ value: p.emailAddress }] : undefined,
				}
		);
};

/** Deduplicate contacts by id */
const dedupeContacts = (contacts: Api.IProjectedContact[]) => {
	return contacts.filter((contact, index) => contacts.findIndex(c => c.id === contact.id) === index);
};

export const MeetingsBoard = () => {
	const userSession = useUserSession();
	const fullscreenModal = useFullscreenModal();
	const { logApiError } = useEventLogging('MeetingsBoard');
	const { impersonationContext } = useAccountContext();

	const buildContactViewModel = (contact: Api.IContact) => {
		const vm = new Api.ContactViewModel(userSession, contact);
		vm.impersonate(impersonationContext);
		return vm;
	};

	// Permissions — sourced from the impersonation context when impersonating, otherwise from the session user
	const schedulerPermissions = impersonationContext?.account?.id
		? impersonationContext.user?.userGroupPermissions?.featurePermissions?.scheduler
		: userSession.featurePermissions?.scheduler;
	const canBrowseAll = !!schedulerPermissions?.viewReporting;
	const canUpdateStatus = !!schedulerPermissions?.write;

	// Search/filter state
	const initialHost = impersonationContext?.user ?? userSession.user ?? null;
	const [searchFieldValue, setSearchFieldValue] = React.useState('');
	const debouncedSearchFieldValue = useDebounceValue(searchFieldValue, 500);
	const [selectedStatuses, setSelectedStatuses] = React.useState<Api.MeetingStatus[]>([
		Api.MeetingStatus.Scheduled,
		Api.MeetingStatus.WaitingForDisposition,
	]);
	const [selectedHost, setSelectedHost] = React.useState<Api.IUser | null>(initialHost);
	const [selectedMinimumDate, setSelectedMinimumDate] = React.useState<string | null>(null);
	const [selectedMaximumDate, setSelectedMaximumDate] = React.useState<string | null>(null);
	const [boardView, setBoardView] = React.useState<BoardView>('list');
	const isCalendarView = boardView === 'calendar';

	// Flyout state
	const [isDetailsOpen, setIsDetailsOpen] = React.useState(false);
	const [meetingIdToView, setMeetingIdToView] = React.useState<string | null>(null);

	const [actionButtonsDisabled, setActionButtonsDisabled] = React.useState(false);

	// A ref (not state) because it's read synchronously right after the await in onSendEmailClicked,
	// before a state update would flush.
	const resolveContactsFailedRef = React.useRef(false);

	const hasSearchValues =
		!!debouncedSearchFieldValue ||
		selectedStatuses.length > 0 ||
		selectedHost?.id !== initialHost?.id ||
		!!selectedMinimumDate;

	// Non-date filter criteria (title / status / host). Shared by the list query and the
	// calendar view — the calendar supplies its own date window, so date bounds stay out here.
	const baseCriteria = React.useMemo(() => {
		const criteria: Api.IScheduledMeetingFilterCriteria[] = [];

		// Title search
		if (debouncedSearchFieldValue) {
			criteria.push({ property: 'Title', value: debouncedSearchFieldValue });
		}

		// Status filter
		if (selectedStatuses.length === 1) {
			criteria.push({ property: 'Status', value: selectedStatuses[0] });
		} else if (selectedStatuses.length > 1) {
			criteria.push({
				op: 'Or',
				criteria: selectedStatuses.map(status => ({ property: 'Status', value: status })),
			});
		}

		// Host filter
		if (selectedHost?.id) {
			criteria.push({ property: 'Host', value: selectedHost.id });
		}

		return criteria;
	}, [debouncedSearchFieldValue, selectedStatuses, selectedHost]);

	// List filter criteria = base + the date-range dropdown bounds.
	const filterRequest = React.useMemo(() => {
		const criteria = [...baseCriteria];

		// Date range filter - adjust dates by 1 day to make range inclusive
		if (selectedMinimumDate) {
			const dayBefore = moment(selectedMinimumDate).subtract(1, 'day').format('YYYY-MM-DD');
			criteria.push({ property: 'StartDate', value: dayBefore, op: 'Gt' });
		}
		if (selectedMaximumDate) {
			const dayAfter = moment(selectedMaximumDate).add(1, 'day').format('YYYY-MM-DD');
			criteria.push({ property: 'StartDate', value: dayAfter, op: 'Lt' });
		}

		return { criteria };
	}, [baseCriteria, selectedMinimumDate, selectedMaximumDate]);

	// Query hooks — the list only fetches in list view; the calendar view fetches its own window.
	const meetingFilterQuery = useScheduledMeetingFilter({
		filterRequest,
		pageSize: 25,
		enabled: !isCalendarView,
		expand: 'participants',
		impersonationContext,
	});

	const exportMeetingsMutation = useExportScheduledMeetingsMutation({
		onError: error => {
			logApiError('ExportMeetings-Error', error);
		},
	});

	const meetingContactsMutation = useScheduledMeetingContactsMutation();

	const meetings = React.useMemo(() => {
		const all = meetingFilterQuery.data?.pages.map(page => page.values).flat() ?? [];
		// Display order: upcoming meetings first (closest-first), then everything else
		// (most-recent-first). A meeting only counts as upcoming if it's still Scheduled
		// AND its startDate is in the future — Cancelled/Completed/WaitingForDisposition
		// always sort into the past bucket regardless of date. Sorted client-side because
		// the ScheduledMeeting/filter endpoint currently ignores sortDirection.
		// Undated meetings: isUpcoming returns false (no startDate), so they land in the
		// past bucket. POSITIVE_INFINITY as the fallback time sorts them last within that
		// bucket (after all dated past meetings) — they have no time reference so they
		// belong at the bottom rather than at the top.
		const now = Date.now();
		const isUpcoming = (m: Api.IScheduledMeeting) => {
			if (m.status !== Api.MeetingStatus.Scheduled) return false;
			if (!m.startDate) return false;
			return new Date(m.startDate).getTime() >= now;
		};
		return [...all].sort((a, b) => {
			const aUp = isUpcoming(a);
			const bUp = isUpcoming(b);
			if (aUp !== bUp) return aUp ? -1 : 1;
			const aTime = a.startDate ? new Date(a.startDate).getTime() : Number.POSITIVE_INFINITY;
			const bTime = b.startDate ? new Date(b.startDate).getTime() : Number.POSITIVE_INFINITY;
			return aUp ? aTime - bTime : bTime - aTime;
		});
	}, [meetingFilterQuery.data?.pages]);

	// Derive the flyout meeting from fresh query data
	const meetingToView = React.useMemo(() => {
		if (!meetingIdToView) return null;
		return meetings.find(m => m.id === meetingIdToView) ?? null;
	}, [meetingIdToView, meetings]);

	// Selection state
	const {
		excludedRows: excludedMeetings,
		selectedRows: selectedMeetings,
		selectionState: meetingSelectionState,
		isAllSelected,
		isPartialSelected,
		isSelectionStateAll,
		isSelected: isMeetingSelected,
		reset: resetMeetingSelection,
		toggleRow: toggleMeetingRow,
		toggleSelectAll: toggleSelectAllMeetings,
	} = useSelectionState({ rows: meetings?.map(m => m.id).filter((id): id is string => !!id) });

	const refreshMeetings = () => {
		invalidateAllScheduledMeetingFilters();
		invalidateAllScheduledMeetingStats();
	};

	const excludedContacts = React.useMemo(() => {
		return dedupeContacts(
			excludedMeetings.flatMap(meetingId => {
				const meeting = meetings.find(m => m.id === meetingId);
				return meeting ? getContactParticipants(meeting) : [];
			})
		);
	}, [excludedMeetings, meetings]);

	const selectedContacts = React.useMemo(() => {
		return dedupeContacts(
			meetings
				.filter(meeting => meeting.id && selectedMeetings.indexOf(meeting.id) > -1)
				.flatMap(meeting => getContactParticipants(meeting))
		);
	}, [meetings, selectedMeetings]);

	const getMatchingContacts = async () => {
		setActionButtonsDisabled(true);
		resolveContactsFailedRef.current = false;

		let results: Api.IProjectedContact[] = [];

		if (isAllSelected) {
			try {
				// Meeting-level exclusion: the server drops the unchecked meetings, so a contact who is
				// still in other selected meetings is retained.
				const apiResults = await meetingContactsMutation.mutateAsync({
					filterRequest,
					excludedMeetingIds: excludedMeetings,
					impersonationContext,
				});

				results = apiResults;
			} catch (error) {
				logApiError('ResolveContacts-Error', error);
				resolveContactsFailedRef.current = true;
			}
		} else {
			results = dedupeContacts(
				meetings
					.filter(meeting => meeting.id && selectedMeetings.indexOf(meeting.id) > -1)
					.flatMap(meeting => getContactParticipants(meeting))
			);
		}

		setActionButtonsDisabled(false);
		return results;
	};

	const onExport = () => {
		exportMeetingsMutation.mutate({ filterRequest, impersonationContext });
	};

	const onSendEmailClicked = async () => {
		const matchingContacts = await getMatchingContacts();

		const guard = getSendEmailGuardResult(resolveContactsFailedRef.current, matchingContacts);
		// Repo tsconfig has strictNullChecks off, so `!guard.canSend` doesn't narrow the discriminated
		// union; the strict-equality form does.
		if (guard.canSend === false) {
			toast.error(guard.message);
			return;
		}

		fullscreenModal.history?.push({
			pathname: '/email/campaigns/create/from-selection',
			state: {
				model: {
					context: {
						allowSenderSelection: true,
						bulkFilterRequestOptions: {
							readOnlySearchCriteria: true,
						},
						contacts: matchingContacts.map(buildContactViewModel),
					},
					type: 'Contacts',
				},
			},
		});
	};

	const clearSearch = () => {
		setSearchFieldValue('');
		setSelectedStatuses([]);
		setSelectedHost(initialHost);
		setSelectedMinimumDate(null);
		setSelectedMaximumDate(null);
		invalidateAllScheduledMeetingFilters();
		invalidateAllScheduledMeetingStats();
	};

	const viewMeetingDetails = (meeting: Api.IScheduledMeeting) => {
		setMeetingIdToView(meeting.id ?? null);
		setIsDetailsOpen(true);
	};

	const isLoading = meetingFilterQuery.isPending;
	const noResultsFound = !isLoading && meetingFilterQuery.isFetched && meetings.length === 0;

	return (
		<Page>
			<MultiContainerHeader
				appBarHeader={
					<BreadcrumbBar
						items={[
							{ label: 'Data Boards', path: ROUTES.dataBoards.home },
							{ label: 'Meetings', path: ROUTES.dataBoards.meetings },
						]}
					/>
				}
			/>

			<Column styles={[styleSheet.container]}>
				<Databoard styles={[styleSheet.databoardContainer]}>
					<DataboardFilters>
						<TextInput
							inputId='meeting-search-input'
							type='text'
							className={css(styleSheet.searchInput)}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchFieldValue(e.target.value)}
							value={searchFieldValue}
							leftAccessory={<SearchIcon className={css(dbStyleSheet.searchIcon)} />}
							placeholder='Search by title...'
						/>

						<div className={css(dbStyleSheet.mediumInputNoShrink)}>
							<MeetingHostFilterDropdown
								selectedHost={selectedHost}
								setSelectedHost={setSelectedHost}
								anchorClassName={css(dbStyleSheet.dropdownReset)}
								readOnly={!canBrowseAll}
							/>
						</div>

						<div className={css(dbStyleSheet.mediumInputNoShrink)}>
							<MeetingStatusFilterDropdown
								selectedStatuses={selectedStatuses}
								setSelectedStatuses={setSelectedStatuses}
								anchorClassName={css(dbStyleSheet.dropdownReset)}
							/>
						</div>

						{/* The calendar view navigates dates itself, so the date-range dropdown is list-only. */}
						{!isCalendarView ? (
							<div className={css(dbStyleSheet.mediumInputNoShrink)}>
								<MeetingDateFilterDropdown
									selectedMinimumDate={selectedMinimumDate}
									setSelectedMinimumDate={setSelectedMinimumDate}
									selectedMaximumDate={selectedMaximumDate}
									setSelectedMaximumDate={setSelectedMaximumDate}
									anchorClassName={css(dbStyleSheet.dropdownReset)}
								/>
							</div>
						) : null}

						{/* Right-aligned trailing cluster: Clear Search + List/Calendar toggle (icon buttons
						    matching the Opportunity board's layout toggle). */}
						<div className={css(styleSheet.trailingActions)}>
							{hasSearchValues ? (
								<button
									className={css(baseStyleSheet.ctaButtonReverse, dbStyleSheet.clearSearchButton)}
									onClick={clearSearch}
								>
									<span>Clear Search</span>
								</button>
							) : null}
							<Button
								variant='ghost'
								size='icon'
								aria-label='List view'
								aria-pressed={!isCalendarView}
								title='List view'
								onClick={() => setBoardView('list')}
							>
								<TableLayoutIcon fillColor={isCalendarView ? navigation : brandPrimary} />
							</Button>
							<Button
								variant='ghost'
								size='icon'
								aria-label='Calendar view'
								aria-pressed={isCalendarView}
								title='Calendar view'
								onClick={() => {
									// The calendar owns its own date window and hides the date-range dropdown, so any
									// list-view date bounds would strand as an invisible filter (still tripping
									// "Clear Search") — drop them on switch.
									setSelectedMinimumDate(null);
									setSelectedMaximumDate(null);
									setBoardView('calendar');
								}}
							>
								<ScheduleMeetingIcon fill={isCalendarView ? brandPrimary : navigation} />
							</Button>
						</div>
					</DataboardFilters>

					<DataboardActions>
						<div className={css(dbStyleSheet.leftHeader)}>
							{/* Row selection / export is list-only. */}
							{!isCalendarView ? (
								<UsersActionBar
									excludedContacts={excludedContacts}
									selectedContacts={selectedContacts}
									getMatchingContacts={getMatchingContacts}
									onExport={onExport}
									selectionState={meetingSelectionState}
									actionButtonsDisabled={actionButtonsDisabled}
									onSendEmailClicked={onSendEmailClicked}
									showKeepInTouch={false}
								/>
							) : null}
						</div>
						<div className={css(dbStyleSheet.rightHeader)} />
					</DataboardActions>

					<DataboardBody
						data-scroll-restoration-id='meetings-page-table'
						hasFooter={!isCalendarView}
						styles={isCalendarView ? [styleSheet.calendarBody] : []}
					>
						{isCalendarView ? (
							<MeetingsCalendarView
								filterCriteria={baseCriteria}
								impersonationContext={impersonationContext}
								canUpdateStatus={canUpdateStatus}
								onMeetingUpdated={refreshMeetings}
							/>
						) : (
							<>
								<DataGrid styles={[dbStyleSheet.tableOverrides]} isLoading={isLoading}>
									<DataGridColumns>
										<DataGridColumn sticky styles={[dbStyleSheet.checkboxColumn]}>
											<Checkbox
												checked={isSelectionStateAll}
												id='select-all-meetings'
												onChange={() => {
													resetMeetingSelection();
													toggleSelectAllMeetings();
												}}
												partial={isPartialSelected}
											/>
										</DataGridColumn>
										<DataGridColumn sticky>Title</DataGridColumn>
										<DataGridColumn sticky>Host</DataGridColumn>
										<DataGridColumn sticky>Date / Time</DataGridColumn>
										<DataGridColumn sticky>Status</DataGridColumn>
										<DataGridColumn sticky>Contacts</DataGridColumn>
										<DataGridColumn sticky>Location</DataGridColumn>
										<DataGridColumn sticky>Source</DataGridColumn>
									</DataGridColumns>

									<DataGridRows>
										{!isLoading
											? meetings.map(meeting => (
													<DataGridRow key={`meeting-${meeting.id}`} styles={[dbStyleSheet.row]}>
														<DataGridRowItem>
															<Checkbox
																readOnly
																checked={isMeetingSelected(meeting.id ?? '')}
																id={meeting.id ?? ''}
																onClick={event => {
																	event.stopPropagation();
																	event.preventDefault();
																	if (meeting.id) {
																		toggleMeetingRow(meeting.id, event.shiftKey);
																	}
																}}
															/>
														</DataGridRowItem>
														<DataGridRowItem styles={[styleSheet.titleCell]}>
															<button onClick={() => viewMeetingDetails(meeting)}>
																<h6 className={css(dbStyleSheet.name, baseStyleSheet.truncateText)}>
																	{meeting.title || 'Untitled'}
																</h6>
																{meeting.meetingConfigName ? (
																	<span className={css(styleSheet.titleSubtitle)} title={meeting.meetingConfigName}>
																		{meeting.meetingConfigName}
																	</span>
																) : null}
															</button>
														</DataGridRowItem>
														<DataGridRowItem>{getHostName(getHost(meeting))}</DataGridRowItem>
														<DataGridRowItem>{formatDateRange(meeting)}</DataGridRowItem>
														<DataGridRowItem>
															{meeting.status && MEETING_STATUS_CONFIG[meeting.status] ? (
																<Badge
																	text={MEETING_STATUS_CONFIG[meeting.status].label}
																	foregroundColor={MEETING_STATUS_CONFIG[meeting.status].color}
																/>
															) : (
																'-'
															)}
														</DataGridRowItem>
														<DataGridRowItem>
															{(() => {
																const contacts = getContactParticipantsFromMeeting(meeting);
																if (contacts.length === 0) return '-';
																const first = contacts[0];
																const rest = contacts.slice(1);
																return (
																	<div className={css(styleSheet.contactsCell)}>
																		{first.contact ? (
																			<EntityChip
																				className={css(dbStyleSheet.contact)}
																				key={first.contactId ?? first.id}
																				entity={buildContactViewModel(first.contact)}
																				readOnly={true}
																				sizeConstraint='compact'
																			/>
																		) : (
																			<span>
																				{`${first.firstName ?? ''} ${first.lastName ?? ''}`.trim() || 'Unknown'}
																			</span>
																		)}
																		{rest.length > 0 ? (
																			<Popover>
																				<PopoverTrigger asChild>
																					<button className={css(styleSheet.moreContactsButton)}>
																						+{rest.length} more
																					</button>
																				</PopoverTrigger>
																				<PopoverContent align='start' styles={[styleSheet.moreContactsPopover]}>
																					{rest.map(cp =>
																						cp.contact ? (
																							<EntityChip
																								className={css(dbStyleSheet.contact)}
																								key={cp.contactId ?? cp.id}
																								entity={buildContactViewModel(cp.contact)}
																								readOnly={true}
																								sizeConstraint='compact'
																							/>
																						) : (
																							<span key={cp.contactId ?? cp.id}>
																								{`${cp.firstName ?? ''} ${cp.lastName ?? ''}`.trim() || 'Unknown'}
																							</span>
																						)
																					)}
																				</PopoverContent>
																			</Popover>
																		) : null}
																	</div>
																);
															})()}
														</DataGridRowItem>
														<DataGridRowItem>
															<LocationDisplay location={meeting.location} />
														</DataGridRowItem>
														<DataGridRowItem>
															{meeting.creationContext?.sourceIdentifier ? (
																<SimpleTooltip title={meeting.creationContext.sourceIdentifier}>
																	<span>{getSourceLabel(meeting)}</span>
																</SimpleTooltip>
															) : (
																getSourceLabel(meeting)
															)}
														</DataGridRowItem>
													</DataGridRow>
												))
											: null}

										{isLoading ? (
											<DataGridRow>
												<DataGridRowItem colSpan={8}>
													<LoadingSpinner type='large' />
												</DataGridRowItem>
											</DataGridRow>
										) : null}
									</DataGridRows>
								</DataGrid>

								{!meetingFilterQuery.isPending &&
								!meetingFilterQuery.isFetchingNextPage &&
								meetingFilterQuery.hasNextPage ? (
									<Waypoint bottomOffset='-200px' onEnter={() => meetingFilterQuery.fetchNextPage()} />
								) : null}

								{noResultsFound ? <DataboardNoResults onClearSearch={clearSearch} /> : null}
							</>
						)}
					</DataboardBody>

					{/* TODO: add stats content when defined. Hidden in calendar view (no footer band). */}
					{!isCalendarView ? <DataboardStats>{null}</DataboardStats> : null}

					{meetingToView ? (
						<MeetingDetailsFlyout
							isOpen={isDetailsOpen}
							setIsOpen={setIsDetailsOpen}
							meeting={meetingToView}
							canUpdateStatus={canUpdateStatus}
							onParticipantStatusUpdated={() => {
								refreshMeetings();
							}}
						/>
					) : null}

					<ExportConfirmationModalV2
						mutationResult={exportMeetingsMutation}
						modalProps={{
							isOpen: !exportMeetingsMutation.isIdle,
							onRequestClose: () => exportMeetingsMutation.reset(),
						}}
						subTitle={
							<>
								<p className={css(dbStyleSheet.noMargin)}>
									The fields we&apos;ll export include: Title, Status, Start Date, End Date, Host, Participants,
									Location
								</p>
							</>
						}
					/>

					<FabContext appearance={{ hidden: true }} />
				</Databoard>
			</Column>
		</Page>
	);
};
